import { useEffect, useRef } from 'react';

interface PrivacyPageProps {
  onClose: () => void;
}

export function PrivacyPage({ onClose }: PrivacyPageProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    closeRef.current?.focus();

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  function handleOverlayClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === overlayRef.current) {
      onClose();
    }
  }

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-sm"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="Privacy notice"
    >
      <div className="relative max-h-[calc(100vh-2rem)] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6 shadow-2xl sm:p-8 dark:bg-gray-900">
        {/* Close button */}
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          aria-label="Close privacy notice"
          className="absolute right-4 top-4 rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-gray-400 dark:hover:bg-gray-800"
        >
          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
          </svg>
        </button>

        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Privacy Notice</h2>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          Plain-language summary of what data Domain Triage collects and why.
        </p>

        <div className="mt-6 space-y-6 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
          <section>
            <h3 className="mb-2 text-base font-semibold text-gray-900 dark:text-white">
              What we collect
            </h3>
            <ul className="list-inside list-disc space-y-2">
              <li>
                <strong>IP addresses</strong> are used solely for rate limiting to prevent abuse.
                They are not stored long-term or shared with third parties.
              </li>
              <li>
                <strong>Domain queries</strong> you submit are cached for 15 minutes to speed up
                repeated checks. After that, they are automatically discarded. We do not keep
                permanent logs of your searches.
              </li>
              <li>
                <strong>Theme preference</strong> (light or dark mode) is saved in your browser's
                localStorage. This data never leaves your device.
              </li>
              <li>
                <strong>Shortlist and notes</strong> are stored in your browser's localStorage. This
                data stays on your device and is never sent to our servers.
              </li>
            </ul>
          </section>

          <section>
            <h3 className="mb-2 text-base font-semibold text-gray-900 dark:text-white">
              What we do not collect
            </h3>
            <ul className="list-inside list-disc space-y-2">
              <li>
                <strong>No analytics or tracking</strong> -- we do not use Google Analytics,
                tracking pixels, or any third-party analytics tools.
              </li>
              <li>
                <strong>No cookies</strong> -- Domain Triage does not set any cookies.
              </li>
              <li>
                <strong>No personal information</strong> -- we do not collect names, email
                addresses, or any other personally identifiable information. There are no user
                accounts.
              </li>
              <li>
                <strong>No data sharing</strong> -- your queries are never sold, shared, or
                provided to any third party.
              </li>
            </ul>
          </section>

          <section>
            <h3 className="mb-2 text-base font-semibold text-gray-900 dark:text-white">
              Third-party services
            </h3>
            <p>
              Domain Triage performs DNS and RDAP lookups on your behalf. These are standard internet
              protocols. When we check domain availability, DNS queries go to public DNS resolvers
              and RDAP queries go to the relevant registry servers. These services may log requests
              according to their own policies.
            </p>
          </section>

          <section>
            <h3 className="mb-2 text-base font-semibold text-gray-900 dark:text-white">
              Data retention
            </h3>
            <p>
              Cached domain results expire automatically after 15 minutes. No permanent storage of
              query data exists. Rate-limiting counters reset periodically and are not tied to any
              persistent identifier.
            </p>
          </section>

          <section>
            <h3 className="mb-2 text-base font-semibold text-gray-900 dark:text-white">
              Questions
            </h3>
            <p>
              If you have questions about this privacy notice, you can reach us via the project
              repository. This notice may be updated occasionally; the most current version will
              always be available within the application.
            </p>
          </section>
        </div>

        <div className="mt-8 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-blue-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
